/**
 * Primitivos do design system.
 *
 * Todos os valores vêm de custom properties geradas por `@nv/tokens` — os
 * mesmos nomes do protótipo. Nenhuma cor ou medida literal aqui dentro.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Icon, type IconName } from './icons';
import { Ilustracao, type NomeIlustracao } from './Ilustracao';
import { Sheet } from './Sheet';

/**
 * Card — elevação por NÍVEL (A2, rodada 3): `surf` é o padrão, flat, separado
 * por fio; `raised` é o único que se levanta (o AGORA, o saldo); `inset` é a
 * superfície recuada (surf2) para um bloco dentro de outro; `chip` e
 * `outline` seguem como antes. `lista` tira o padding: as linhas (`Row`) é que
 * carregam o ar, e a última não tem divisor.
 */
export function Card({
  children,
  variant = 'surf',
  lista = false,
  className = '',
  'data-testid': testId,
}: {
  children: ReactNode;
  variant?: 'surf' | 'raised' | 'inset' | 'chip' | 'outline';
  lista?: boolean;
  className?: string;
  /** Gancho para os roteiros de aceite. */
  'data-testid'?: string;
}) {
  return (
    <div className={`nv-card nv-card--${variant}${lista ? ' nv-card--lista' : ''} ${className}`.trim()} data-testid={testId}>
      {children}
    </div>
  );
}

/**
 * Linha de lista (A2): ícone em caixa 36 · título 15/700 + detalhe 13 ·
 * trailing (seta, valor, chip). Mínimo 56px — o dedo acerta. Vira botão,
 * link ou só texto conforme o que recebe. Dentro de `<Card lista>`, o
 * divisor é um só, entre linhas.
 */
export function Row({
  icon,
  avatar,
  title,
  detail,
  trailing,
  to,
  onClick,
  href,
  className = '',
  'data-testid': testId,
  disabled,
}: {
  icon?: IconName;
  /** Substitui o ícone (avatar, miniatura). */
  avatar?: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  /** Padrão: seta quando a linha navega; nada quando é só texto. */
  trailing?: ReactNode;
  to?: string;
  onClick?: () => void;
  href?: string;
  className?: string;
  'data-testid'?: string;
  disabled?: boolean;
}) {
  const interativa = !!(to || onClick || href);
  const conteudo = (
    <>
      {avatar ?? (icon && (
        <span className="nv-row__icone" aria-hidden="true">
          <Icon name={icon} size={20} />
        </span>
      ))}
      <span className="nv-row__texto">
        <span className="nv-row__titulo">{title}</span>
        {detail && <span className="nv-row__detalhe">{detail}</span>}
      </span>
      {trailing !== undefined ? trailing : interativa ? <Icon name="avancar" size={18} /> : null}
    </>
  );
  const classes = `nv-row ${className}`.trim();
  if (to) {
    return (
      <Link to={to} className={classes} data-testid={testId}>
        {conteudo}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} data-testid={testId} target="_blank" rel="noopener noreferrer">
        {conteudo}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={classes} onClick={onClick} data-testid={testId} disabled={disabled}>
        {conteudo}
      </button>
    );
  }
  return (
    <div className={classes} data-testid={testId}>
      {conteudo}
    </div>
  );
}

/**
 * Segmentado (Minha viagem · Família · Grupo). Refino final (23/09/2026): uma
 * pílula desliza até a opção escolhida em 220ms, em vez de o fundo trocar de
 * botão. `tom="marca"` (padrão) é a pílula laranja com texto branco;
 * `tom="claro"` é a pílula branca com sombra leve (Grupo, Bagagem). Sem valor
 * escolhido — a moeda da viagem nova não tem padrão —, não há pílula.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  className = '',
  tom = 'marca',
}: {
  value: T | null | undefined;
  options: { valor: T; rotulo: string; testId?: string }[];
  onChange: (v: T) => void;
  label: string;
  className?: string;
  tom?: 'marca' | 'claro';
}) {
  const indice = options.findIndex((o) => o.valor === value);
  return (
    <div
      className={`nv-segmented nv-segmented--${tom} ${className}`.trim()}
      role="radiogroup"
      aria-label={label}
      style={{ ['--seg-n' as string]: options.length, ['--seg-i' as string]: Math.max(0, indice) }}
    >
      {indice >= 0 && <span className="nv-segmented__pilula" aria-hidden="true" />}
      {options.map((o) => (
        <button type="button" key={o.valor} role="radio" aria-checked={value === o.valor} className={value === o.valor ? 'nv-seg nv-seg--ativo' : 'nv-seg'} onClick={() => onChange(o.valor)} data-testid={o.testId}>
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

/** Texto de apoio abaixo de um bloco (o que era `auth-note` fora da autenticação). */
export function Note({ children, className = '', role }: { children: ReactNode; className?: string; role?: 'status' | 'alert' }) {
  return (
    <p className={`nv-note ${className}`.trim()} role={role}>
      {children}
    </p>
  );
}

/**
 * Folha de confirmação ("Excluir a atividade?"). Nunca `window.confirm`: o
 * diálogo nativo bloqueia o app e ignora tema e safe area.
 *
 * Refino final (23/09/2026): ilustração ou ícone num quadrado de 56, título
 * 20/900, frase, e dois botões de 48 lado a lado — Cancelar (chip) e a ação
 * (marca; perigo quando é exclusão).
 */
export function ConfirmSheet({
  titulo,
  texto,
  confirmar,
  ocupado = false,
  onConfirm,
  onCancel,
  testId = 'confirmar',
  tom = 'perigo',
  ilustracao,
  icone,
  children,
}: {
  titulo: string;
  texto?: ReactNode;
  confirmar: string;
  ocupado?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  testId?: string;
  /** `perigo` (padrão) para o irreversível; `normal` para confirmar uma ação comum, como convidar. */
  tom?: 'perigo' | 'normal';
  /** Desenho do topo; sem ele, o ícone (ou o padrão do tom) num quadrado de 56. */
  ilustracao?: NomeIlustracao;
  icone?: IconName;
  /** Conteúdo extra entre a frase e os botões (ex.: campo "digite o nome"). */
  children?: ReactNode;
}) {
  const iconePadrao: IconName = icone ?? (tom === 'perigo' ? 'excluir' : 'concluir');
  return (
    <Sheet titleId="confirm-titulo" onFechar={onCancel} testId={testId} className="sheet__panel--confirmar">
      {ilustracao ? (
        <Ilustracao nome={ilustracao} tamanho={64} className="sheet__ilustracao" />
      ) : (
        <span className={`sheet__selo sheet__selo--${tom}`} aria-hidden="true">
          <Icon name={iconePadrao} size={26} />
        </span>
      )}
      <h2 className="sheet__title" id="confirm-titulo">{titulo}</h2>
      {texto && <p className="sheet__text">{texto}</p>}
      {children}
      <div className="sheet__acoes">
        <button type="button" className="nv-btn nv-btn--secondary" onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" className={`nv-btn ${tom === 'perigo' ? 'nv-btn--danger' : 'nv-btn--primary'}`} disabled={ocupado} onClick={onConfirm} data-testid={`${testId}-sim`}>
          {confirmar}
        </button>
      </div>
    </Sheet>
  );
}

/**
 * Cabeçalho de tela — regra do sistema (rodada 3, A1): todo cabeçalho
 * começa a `--header-top` do topo; a safe area do aparelho já foi descontada
 * pelo `.app-scroll`, então nenhuma tela sabe de notch. É um `.dia-header`
 * por dentro: os roteiros e os estilos antigos continuam valendo.
 *
 * `eyebrow` é a linha curta acima do título (a data do dia); `sub` vem
 * abaixo; `trailing` é a ação da direita (chip, reordenar, sino).
 *
 * Sem botão "‹" (teste físico, item 19): voltar é do sistema — gesto e botão
 * no Android; no iOS instalado, arrastar da borda (`gestosDeApp.ts`).
 */
export function ScreenHeader({
  title,
  eyebrow,
  sub,
  trailing,
  leading,
  className = '',
  titleId,
}: {
  title: ReactNode;
  /** Antes do título (ilustração da tela, como na Emergência). */
  leading?: ReactNode;
  eyebrow?: ReactNode;
  sub?: ReactNode;
  trailing?: ReactNode;
  className?: string;
  titleId?: string;
}) {
  return (
    <header className={`dia-header ${className}`.trim()}>
      {leading}
      <div className="dia-header__titles">
        {eyebrow && <span className="dia-header__date">{eyebrow}</span>}
        <h1 className="dia-header__title" id={titleId}>
          {title}
        </h1>
        {sub}
      </div>
      {trailing}
    </header>
  );
}

/**
 * Título de bloco. Decisão do PO (17/09/2026, lote de aparência 1): caixa
 * normal. Muitas telas ainda passam o texto em CAIXA ALTA (era o estilo
 * antigo); aqui ele vira "Caixa normal" sem tocar em cada tela. Texto que já
 * vem misto passa intacto.
 */
export function tituloDeBloco(texto: string): string {
  const t = texto.trim();
  if (t === '' || t !== t.toLocaleUpperCase('pt-BR')) return texto;
  const baixo = t.toLocaleLowerCase('pt-BR');
  return baixo.charAt(0).toLocaleUpperCase('pt-BR') + baixo.slice(1);
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="nv-section-label">{typeof children === 'string' ? tituloDeBloco(children) : children}</div>;
}

export function ProgressBar({ percent, label }: { percent: number; label: string }) {
  const valor = Math.max(0, Math.min(100, percent));
  return (
    <div
      className="nv-progress"
      role="progressbar"
      aria-valuenow={valor}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className="nv-progress__fill" style={{ width: `${valor}%` }} />
    </div>
  );
}

export interface AvatarPerson {
  id: string;
  initials: string;
  name: string;
  color: string;
  /** Foto da conta, quando a pessoa pôs uma (URL assinada). */
  url?: string | null;
}

export function Avatar({ person, size = 26 }: { person: AvatarPerson; size?: number }) {
  return (
    <span
      className={person.url ? 'nv-avatar foto-avatar foto-avatar--foto' : 'nv-avatar'}
      style={{ width: size, height: size, background: person.url ? undefined : person.color, fontSize: size * 0.4 }}
      // A cor da família é informação redundante: o nome acessível carrega
      // o dado, para que estado nunca dependa só de cor.
      aria-label={person.name}
      role="img"
    >
      {person.url ? <img src={person.url} alt="" loading="lazy" /> : person.initials}
    </span>
  );
}

export function AvatarStack({ people, size = 26 }: { people: AvatarPerson[]; size?: number }) {
  return (
    <span className="nv-avatar-stack" style={{ ['--avatar-size' as string]: `${size}px` }}>
      {people.map((person) => (
        <Avatar key={person.id} person={person} size={size} />
      ))}
    </span>
  );
}

export function MetricCard({
  label,
  value,
  suffix,
  note,
  accent,
  onClick,
}: {
  label: string;
  value: string;
  /** Parte menor à direita do valor, como o "/19" de "17/19". */
  suffix?: string;
  note?: ReactNode;
  /** Papel da cor — nunca o nome dela. */
  accent: 'success' | 'danger' | 'info' | 'brand' | 'violet' | 'neutral';
  /**
   * Com destino, o card inteiro é o botão. É o próprio card que vira
   * `<button>` — embrulhá-lo por fora quebraria o `flex: 1` da fileira.
   */
  onClick?: () => void;
}) {
  const conteudo = (
    <>
      <div className="nv-metric__label">{label}</div>
      <div className="nv-metric__value">
        {value}
        {suffix && <span className="nv-metric__suffix">{suffix}</span>}
      </div>
      {note && <div className="nv-metric__note">{note}</div>}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={`nv-metric nv-metric--${accent} nv-metric--acao`}
        onClick={onClick}
      >
        {conteudo}
      </button>
    );
  }

  return <div className={`nv-metric nv-metric--${accent}`}>{conteudo}</div>;
}

export function ShortcutRow({
  icon,
  label,
  detail,
  onClick,
}: {
  icon: string;
  label: string;
  detail?: string;
  onClick?: () => void;
}) {
  return (
    <button type="button" className="nv-shortcut" onClick={onClick}>
      <span className="nv-shortcut__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="nv-shortcut__label">{label}</span>
      {detail && <span className="nv-shortcut__detail">{detail}</span>}
      <span className="nv-shortcut__chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

/* Estados — os quatro que o protótipo define. */

/**
 * Skeleton (refino final): repete a forma do que vai chegar, com os mesmos
 * raios, e um brilho que atravessa da esquerda para a direita (1,4s).
 */
export function Skeleton({
  height,
  count = 1,
  raio = 'card',
  gap,
}: {
  height: number;
  count?: number;
  /** O raio da peça que ele antecipa. */
  raio?: 'hero' | 'card' | 'control' | 'tile' | 'chip';
  gap?: number;
}) {
  return (
    <div className="nv-skeleton-group" aria-hidden="true" style={gap !== undefined ? { gap } : undefined}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={`nv-skeleton nv-skeleton--${raio}`} style={{ height }} />
      ))}
    </div>
  );
}

/**
 * Estado vazio (refino final): ilustração de 120 + título 18/900 + frase 14
 * (até 240px) + uma ação em pílula da marca. Sem ilustração, o ícone antigo
 * (numa caixa) segue funcionando para as telas ainda não refinadas.
 */
export function EmptyState({
  icon,
  ilustracao,
  title,
  description,
  action,
  secondaryAction,
}: {
  /** Um `<Icon>` da Fundação Visual; string (emoji) só onde ainda não houver ícone. */
  icon?: ReactNode;
  ilustracao?: NomeIlustracao;
  title: string;
  description: string;
  action?: { label: string; onClick: () => void };
  /** Segunda saída, quando há duas (ex.: sem viagens: criar · entrar com convite). */
  secondaryAction?: { label: string; onClick: () => void };
}) {
  return (
    <div className={ilustracao ? 'nv-empty nv-empty--ilustrado' : 'nv-empty'}>
      {ilustracao ? (
        <Ilustracao nome={ilustracao} tamanho={120} className="nv-empty__ilustracao" />
      ) : (
        <span className="nv-empty__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <h2 className="nv-empty__title">{title}</h2>
      <p className="nv-empty__text">{description}</p>
      {(action || secondaryAction) && (
        <div className="nv-empty__acoes">
          {action && (
            <button type="button" className="nv-btn nv-btn--primary nv-btn--pilula" onClick={action.onClick}>
              {action.label}
            </button>
          )}
          {secondaryAction && (
            <button type="button" className="nv-btn nv-btn--secondary nv-btn--pilula" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function ErrorState({
  title,
  description,
  onRetry,
}: {
  title: string;
  description: string;
  onRetry: () => void;
}) {
  return (
    <div className="nv-error" role="alert">
      <h2 className="nv-error__title">
        <Icon name="aviso" size={20} />
        {title}
      </h2>
      {/* O texto diz o que continua seguro — não só que falhou. */}
      <p className="nv-error__text">{description}</p>
      <button type="button" className="nv-btn nv-btn--secondary" onClick={onRetry}>
        Tentar novamente
      </button>
    </div>
  );
}

export function OfflineBanner() {
  return (
    <div className="nv-offline" role="status">
      <Icon name="offline" size={16} />
      Sem conexão · alterações sincronizam depois
    </div>
  );
}
